import React, { useState, useRef, useCallback, ReactElement } from 'react';
import { Box, BoxProps } from '@mui/material';

export interface IInfiniteScrollerProps extends Omit<BoxProps, 'onWheel'> {
  children: React.ReactNode;
  hasMore: boolean;
  loadMore: CallableFunction;
  loader?: React.ReactElement;
  }

const InfiniteScroller: React.FC<IInfiniteScrollerProps> = ({
  children,
  hasMore,
  loadMore,
  loader,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [showLoader, setShowLoader] = useState(false);

  const handleScroll = useCallback(async () => {
    if (!hasMore) return;
    const container = containerRef.current;
    const scroller = scrollerRef.current;

    if (
      container &&
      scroller &&
      scroller.scrollHeight - container.scrollTop - container.clientHeight < 2
    ) {
      setShowLoader(true);
      await loadMore();
      setShowLoader(false);
    }
  }, [containerRef, scrollerRef, loadMore, hasMore]);

  return (
    <Box
      ref={containerRef}
      {...props}
      sx={{
        '&::-webkit-scrollbar': { display: 'none' },
        ...props.sx,
        overflowY: 'auto',
      }}
      onWheel={handleScroll}
    >
      <Box
        id={`${props.id}-scroller`}
        data-testid={`${props.id}-scroller`}
        ref={scrollerRef}
        sx={{ overflowY: 'auto' }}
      >
        <Box
          id={`${props.id}-content-wrapper`}
          data-testid={`${props.id}-content-wrapper`}
        >
          {children}
        </Box>
        {showLoader && loader}
      </Box>
    </Box>
  );
};

export default InfiniteScroller;
